import { Badge, Box, Card, Code, Flex, Grid, Heading, Text } from '@workos-inc/design-system';
import Head from 'next/head';
import NextLink from 'next/link';
import { Header } from 'src/components/header';
import { NavigationWrapper } from 'src/components/navigation-wrapper';
import { PageBackground } from 'src/components/page-background';
import { PageLayout } from 'src/components/page-layout';
import { Subtitle } from 'src/components/subtitle';
import { DotNetLogoIcon } from 'src/icons/dotnet-logo-icon';
import { ElixirLogoIcon } from 'src/icons/elixir-logo-icon';
import { GoLogoIcon } from 'src/icons/go-logo-icon';
import { JavascriptLogoIcon } from 'src/icons/javascript-logo-icon';
import { KotlinLogoIcon } from 'src/icons/kotlin-logo-icon';
import { LaravelLogoIcon } from 'src/icons/laravel-logo-icon';
import { NextJsLogoIcon } from 'src/icons/nextjs-logo-icon';
import { NodeJsLogoIcon } from 'src/icons/nodejs-logo-icon';
import { PhpLogoIcon } from 'src/icons/php-logo-icon';
import { PythonLogoIcon } from 'src/icons/python-logo-icon';
import { ReactLogoIcon } from 'src/icons/react-logo-icon';
import { RemixLogoIcon } from 'src/icons/remix-logo-icon';
import { RubyLogoIcon } from 'src/icons/ruby-logo-icon';
import { TanStackLogoIcon } from 'src/icons/tanstack-logo-icon';
import { AUTHKIT_SDK_META, SDK_META } from 'src/utils/constants';
import { createPageImage, createPageTitle } from 'src/utils/create-page-metadata';
import { formatDate } from 'src/utils/format-date';
import { makeOctokit } from 'src/utils/octokit';
type WithReleaseInfo<T> = T & {
  publishedAt: string;
  version: string;
};
type SdkMeta = WithReleaseInfo<(typeof SDK_META)[number]>;
type AuthKitSdkMeta = WithReleaseInfo<(typeof AUTHKIT_SDK_META)[number]>;
async function loadCachedSDKData() {
  const fs = await import('fs');
  const path = await import('path');
  const cacheFile = path.join(process.cwd(), '.sdk-cache', 'releases.json');
  if (!fs.existsSync(cacheFile)) {
    throw new Error('No cached SDK data found');
  }
  const cacheData = JSON.parse(fs.readFileSync(cacheFile, 'utf8'));

  // Filter out SDKs with errors and return only successful ones
  const validSDKs = cacheData.sdks.filter((sdk: {
    error?: string;
    version?: string;
    publishedAt?: string;
  }) => !sdk.error && sdk.version && sdk.publishedAt);
  if (validSDKs.length === 0) {
    throw new Error('No valid SDK data in cache');
  }

  // eslint-disable-next-line no-console
  console.log(`📦 Loaded ${validSDKs.length} SDKs from cache (generated: ${cacheData.generatedAt})`);
  return validSDKs;
}
async function fetchFreshSDKData() {
  const octokit = makeOctokit();
  const repositories = SDK_META.map(async sdk => {
    const {
      data
    } = await octokit.repos.listReleases({
      owner: 'workos',
      repo: sdk.repo
    });
    if (!data || data.length === 0) {
      throw new Error(`No releases found for workos/${sdk.repo}. This likely indicates a GitHub API issue or the repository has no releases.`);
    }
    const latestIndex = Math.max(data.findIndex(d => !d.prerelease), 0);
    const latestRelease = data[latestIndex];
    if (!latestRelease || !latestRelease.name || !latestRelease.published_at) {
      throw new Error(`Invalid release data for workos/${sdk.repo}. Latest release is missing required fields.`);
    }
    return {
      ...sdk,
      version: latestRelease.name,
      publishedAt: latestRelease.published_at
    };
  });
  return Promise.all(repositories);
}
async function fetchAuthKitSDKData(): Promise<AuthKitSdkMeta[]> {
  const octokit = makeOctokit();
  const repositories = AUTHKIT_SDK_META.map(async sdk => {
    try {
      const {
        data
      } = await octokit.repos.listReleases({
        owner: 'workos',
        repo: sdk.repo
      });
      if (!data || data.length === 0) {
        // eslint-disable-next-line no-console
        console.warn(`No releases found for workos/${sdk.repo}`);
        return {
          ...sdk,
          version: '',
          publishedAt: ''
        };
      }
      const latestIndex = Math.max(data.findIndex(d => !d.prerelease), 0);
      const latestRelease = data[latestIndex];
      return {
        ...sdk,
        version: latestRelease?.tag_name ?? latestRelease?.name ?? '',
        publishedAt: latestRelease?.published_at ?? ''
      };
    } catch (error) {
      // eslint-disable-next-line no-console
      console.warn(`Failed to fetch release for workos/${sdk.repo}:`, error instanceof Error ? error.message : 'Unknown error');
      return {
        ...sdk,
        version: '',
        publishedAt: ''
      };
    }
  });
  return Promise.all(repositories);
}
export async function getStaticProps() {
  let sdks: SdkMeta[];
  try {
    // Try to load from cache first (faster builds)
    sdks = await loadCachedSDKData();
    // eslint-disable-next-line no-console
    console.log('✅ Using cached SDK data');
  } catch (cacheError) {
    // eslint-disable-next-line no-console
    console.warn('⚠️  Cache miss, fetching fresh SDK data:', cacheError instanceof Error ? cacheError.message : 'Unknown error');
    try {
      // Fallback to fresh API calls
      sdks = await fetchFreshSDKData();
      // eslint-disable-next-line no-console
      console.log('✅ Fetched fresh SDK data');
    } catch (apiError) {
      // eslint-disable-next-line no-console
      console.error('❌ Failed to fetch fresh SDK data:', apiError instanceof Error ? apiError.message : 'Unknown error');

      // In production, fail the build. In development, we might want to be more lenient.
      if (process.env.NODE_ENV === 'production') {
        throw new Error('Build failed: Unable to fetch SDK data from cache or GitHub API. ' + `Cache error: ${cacheError instanceof Error ? cacheError.message : 'Unknown cache error'}. ` + `API error: ${apiError instanceof Error ? apiError.message : 'Unknown API error'}.`);
      } else {
        // Development fallback: return empty SDKs with error indicator
        // eslint-disable-next-line no-console
        console.warn('🔧 Development mode: continuing with empty SDK list');
        sdks = [];
      }
    }
  }

  // Fetch AuthKit SDK data (non-blocking, failures won't break the build)
  let authkitSdks: AuthKitSdkMeta[] = [];
  try {
    authkitSdks = await fetchAuthKitSDKData();
    // eslint-disable-next-line no-console
    console.log(`✅ Fetched ${authkitSdks.length} AuthKit SDK releases`);
  } catch (error) {
    // eslint-disable-next-line no-console
    console.warn('⚠️  Failed to fetch AuthKit SDK data:', error instanceof Error ? error.message : 'Unknown error');
  }
  const title = 'SDKs';
  const description = 'Official open-source client libraries for your favorite platforms.';
  const image = await createPageImage('SDKs', description);
  return {
    props: {
      sdks,
      authkitSdks,
      pageMeta: {
        title,
        canonicalUrl: 'https://workos.com/docs/sdks',
        description,
        image
      }
    }
  };
}
interface SdksPageProps {
  sdks: SdkMeta[];
  authkitSdks: AuthKitSdkMeta[];
  pageMeta: {
    title: string;
    canonicalUrl: string;
    description: string;
    image: string;
  };
}
const SdksPage = ({
  pageMeta,
  sdks = [],
  authkitSdks = []
}: SdksPageProps) => {
  const title = createPageTitle(pageMeta.title);
  const {
    description,
    image
  } = pageMeta;
  return <Box position="relative" data-sentry-element="Box" data-sentry-component="SdksPage" data-sentry-source-file="index.tsx">
      <Head data-sentry-element="Head" data-sentry-source-file="index.tsx">
        <title>{title}</title>
        <meta content={description} name="description" data-sentry-element="meta" data-sentry-source-file="index.tsx" />
        <meta content={image} itemProp="image" data-sentry-element="meta" data-sentry-source-file="index.tsx" />
        <meta content={image} name="image" data-sentry-element="meta" data-sentry-source-file="index.tsx" />
        <meta content={description} name="og:description" data-sentry-element="meta" data-sentry-source-file="index.tsx" />
        <meta content={image} property="og:image" data-sentry-element="meta" data-sentry-source-file="index.tsx" />
        <meta content={title} property="og:title" data-sentry-element="meta" data-sentry-source-file="index.tsx" />
        <meta content="website" property="og:type" data-sentry-element="meta" data-sentry-source-file="index.tsx" />
        <meta content="summary_large_image" name="twitter:card" data-sentry-element="meta" data-sentry-source-file="index.tsx" />
        <meta content={description} name="twitter:description" data-sentry-element="meta" data-sentry-source-file="index.tsx" />
        <meta content={image} name="twitter:image" data-sentry-element="meta" data-sentry-source-file="index.tsx" />
        <meta content={title} name="twitter:title" data-sentry-element="meta" data-sentry-source-file="index.tsx" />
        <link href={pageMeta.canonicalUrl} rel="canonical" />

        {sdks.map(sdk => <link key={sdk.key} href={`https://images.workoscdn.com/docs/sdks/hero-${sdk.key}.jpg`} rel="prefetch" />)}
      </Head>

      <PageBackground data-sentry-element="PageBackground" data-sentry-source-file="index.tsx" />

      <Header caption="SDKs" data-sentry-element="Header" data-sentry-source-file="index.tsx" />

      <PageLayout.Root layout="index" data-sentry-element="PageLayout.Root" data-sentry-source-file="index.tsx">
        <PageLayout.Nav data-sentry-element="PageLayout.Nav" data-sentry-source-file="index.tsx">
          <NavigationWrapper data-sentry-element="NavigationWrapper" data-sentry-source-file="index.tsx" />
        </PageLayout.Nav>

        <PageLayout.Content data-sentry-element="PageLayout.Content" data-sentry-source-file="index.tsx">
          <Box mb={{
          initial: '5',
          sm: '6',
          lg: '7'
        }} data-sentry-element="Box" data-sentry-source-file="index.tsx">
            <Heading as="h1" mb="2" size={{
            initial: '7',
            sm: '8'
          }} data-sentry-element="Heading" data-sentry-source-file="index.tsx">
              SDKs
            </Heading>
            <Subtitle data-sentry-element="Subtitle" data-sentry-source-file="index.tsx">{description}</Subtitle>
          </Box>

          <Heading as="h2" mb="5" size="6" data-sentry-element="Heading" data-sentry-source-file="index.tsx">
            Backend SDKs
          </Heading>

          <Grid columns={{
          sm: '2',
          md: '3'
        }} gap="4" mb="8" data-sentry-element="Grid" data-sentry-source-file="index.tsx">
            {sdks.filter(({
            experimental
          }) => !experimental).map(({
            key,
            name,
            version,
            repo,
            publishedAt
          }) => <Card key={key} asChild size={{
            initial: '3',
            md: '4'
          }} variant="classic">
                  <NextLink href={`/sdks/${key}`}>
                    <Flex align="center" justify="between">
                      <Flex align="center" gap="4">
                        <Box>
                          {key === 'dotnet' && <DotNetLogoIcon height="40" width="40" />}
                          {key === 'go' && <GoLogoIcon height="40" width="40" />}
                          {key === 'java' && <KotlinLogoIcon height="40" width="40" />}
                          {key === 'laravel' && <LaravelLogoIcon height="40" width="40" />}
                          {key === 'node' && <NodeJsLogoIcon height="40" width="40" />}
                          {key === 'php' && <PhpLogoIcon height="40" width="40" />}
                          {key === 'python' && <PythonLogoIcon height="40" width="40" />}
                          {key === 'ruby' && <RubyLogoIcon height="40" width="40" />}
                        </Box>
                        <Box>
                          <Heading as="h3" size="3">
                            {name}
                          </Heading>
                          <Text as="div" color="gray" size="2">
                            <Code variant="ghost">{repo}</Code>
                          </Text>
                        </Box>
                      </Flex>

                      {version && <Flex align="end" direction="column" gap="1">
                          <Flex>
                            <Badge color="green" mr="-1">
                              {version}
                            </Badge>
                          </Flex>
                          <Text color="gray" size="1">
                            on {formatDate(publishedAt)}
                          </Text>
                        </Flex>}
                    </Flex>
                  </NextLink>
                </Card>)}
          </Grid>

          <Heading as="h2" mb="5" size="6" data-sentry-element="Heading" data-sentry-source-file="index.tsx">
            AuthKit SDKs
          </Heading>

          <Grid columns={{
          sm: '2',
          md: '3'
        }} gap="4" mb="8" data-sentry-element="Grid" data-sentry-source-file="index.tsx">
            {authkitSdks.map(({
            key,
            name,
            repo,
            version,
            publishedAt
          }) => <Card key={key} asChild size={{
            initial: '3',
            md: '4'
          }} variant="classic">
                <NextLink href={`/sdks/${key}`}>
                  <Flex align="center" justify="between">
                    <Flex align="center" gap="4">
                      <Box>
                        {key === 'authkit-js' && <JavascriptLogoIcon height="40" width="40" />}
                        {key === 'authkit-react' && <ReactLogoIcon height="40" width="40" />}
                        {key === 'authkit-nextjs' && <NextJsLogoIcon height="40" width="40" />}
                        {key === 'authkit-remix' && <RemixLogoIcon height="40" width="40" />}
                        {key === 'authkit-react-router' && <ReactLogoIcon height="40" width="40" />}
                        {key === 'authkit-tanstack-start' && <TanStackLogoIcon height="40" width="40" />}
                      </Box>
                      <Box>
                        <Heading as="h3" size="3">
                          {name}
                        </Heading>
                        <Text as="div" color="gray" size="2">
                          <Code variant="ghost">{repo}</Code>
                        </Text>
                      </Box>
                    </Flex>

                    {version && <Flex align="end" direction="column" gap="1">
                        <Flex>
                          <Badge color="green" mr="-1">
                            {version}
                          </Badge>
                        </Flex>
                        <Text color="gray" size="1">
                          on {formatDate(publishedAt)}
                        </Text>
                      </Flex>}
                  </Flex>
                </NextLink>
              </Card>)}
          </Grid>

          <Heading as="h2" mb="5" size="6" data-sentry-element="Heading" data-sentry-source-file="index.tsx">
            Experimental libraries
          </Heading>

          <Grid columns={{
          sm: '2',
          md: '3'
        }} gap="4" mb="8" data-sentry-element="Grid" data-sentry-source-file="index.tsx">
            {sdks.filter(({
            experimental
          }) => experimental).map(({
            key,
            name,
            version,
            repo,
            publishedAt
          }) => <Card key={key} asChild size={{
            initial: '3',
            md: '4'
          }} variant="classic">
                  <NextLink href={`/sdks/${key}`}>
                    <Flex align="center" justify="between">
                      <Flex align="center" gap="4">
                        <Box>
                          {key === 'elixir' && <ElixirLogoIcon height="40" width="40" />}
                        </Box>
                        <Box>
                          <Heading as="h3" size="3">
                            {name}
                          </Heading>
                          <Text as="div" color="gray" size="2">
                            <Code variant="ghost">{repo}</Code>
                          </Text>
                        </Box>
                      </Flex>

                      {version && <Flex align="end" direction="column" gap="1">
                          <Flex>
                            <Badge color="gray" mr="-1">
                              {version}
                            </Badge>
                          </Flex>
                          <Text color="gray" size="1">
                            on {formatDate(publishedAt)}
                          </Text>
                        </Flex>}
                    </Flex>
                  </NextLink>
                </Card>)}
          </Grid>
        </PageLayout.Content>
      </PageLayout.Root>
    </Box>;
};
export default SdksPage;